const ACTIVE_USER_KEY = 'xph_storage_active_user_id';
const LOCAL_SNAPSHOT_PREFIX = 'xph_storage_snapshot_local_';
const SESSION_SNAPSHOT_PREFIX = 'xph_storage_snapshot_session_';

const UNSCOPED_EXACT_KEYS = new Set([
  'token',
  'user',
  'auth_token',
  'user_data',
  'currentProduct',
  'demo_mode',
  'pendingUser',
  'newUser',
  'pendingInviteToken',
  'companyProfile',
  'xph_last_page',
  ACTIVE_USER_KEY,
]);

const UNSCOPED_PREFIXES = [
  'xph_storage_',
  // Onboarding "seen" flags are already keyed by user id, so they don't
  // need snapshot/restore scoping — and MUST NOT ride in the snapshot:
  // when it exceeds MAX_SNAPSHOT_BYTES it is dropped wholesale, wiping
  // these flags and replaying the first-run tours on every login.
  'xph_welcome_video_done_',
  'xph_sidebar_tour_done_',
  'xph_module_intro_seen_',
  'xph_guided_tour_done_',
  'xph_upgrade_nudge_',
];

function shouldUserScopeKey(key) {
  if (typeof key !== 'string' || !key) return false;
  if (UNSCOPED_EXACT_KEYS.has(key)) return false;
  return !UNSCOPED_PREFIXES.some((prefix) => key.startsWith(prefix));
}

function listScopedEntries(storage) {
  const entries = {};
  try {
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (!shouldUserScopeKey(key)) continue;
      const value = storage.getItem(key);
      if (value != null) entries[key] = value;
    }
  } catch { /* ignore — Safari Private Mode etc. */ }
  return entries;
}

function clearScopedEntries(storage) {
  const keysToRemove = [];
  try {
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (shouldUserScopeKey(key)) keysToRemove.push(key);
    }
  } catch { /* ignore */ }
  keysToRemove.forEach((key) => {
    try { storage.removeItem(key); } catch { /* ignore */ }
  });
}

// Hard upper bound on the JSON-serialized size of a snapshot we will
// attempt to persist. Browsers cap localStorage at ~5–10 MB total per
// origin; the snapshot is just ONE key inside that budget, and we
// also need headroom for the active (un-scoped) keys plus the OTHER
// user's snapshot. 1.5 MB per snapshot keeps us comfortably under
// quota in every browser we support, including Safari (which has
// the smallest cap and the most aggressive eviction).
//
// When the candidate snapshot exceeds this limit, we drop it on the
// floor instead of trying to write — a missing snapshot just means
// scoped preferences fall back to defaults, which is FAR better than
// throwing QuotaExceededError up the stack and breaking sign-in. See
// May 27 2026 sign-in bounce-back regression: a 5 MB+ snapshot from
// a heavy analytics user surfaced as `Token validation failed:
// QuotaExceededError` and locked them out of /dashboard entirely.
const MAX_SNAPSHOT_BYTES = 1_500_000;

function safeRemove(storage, key) {
  try { storage.removeItem(key); } catch { /* ignore */ }
}

function safeSet(storage, key, value) {
  try {
    storage.setItem(key, value);
    return true;
  } catch (e) {
    // QuotaExceededError or any storage failure must NEVER bubble up
    // into the auth flow. We log once for diagnostics and move on.
    try {
      // eslint-disable-next-line no-console
      console.warn('[storageScope] setItem failed (quota or storage error); continuing without write', {
        key,
        valueBytes: typeof value === 'string' ? value.length : null,
        error: e?.name || String(e),
      });
    } catch { /* ignore */ }
    return false;
  }
}

function persistSnapshot(userId) {
  if (!userId) return;
  // The whole function MUST be best-effort. Storage failures cannot
  // be allowed to throw out of here — every caller is in a hot auth
  // path where a thrown exception is observed by the user as a hard
  // sign-in bounce.
  try {
    const localEntries = listScopedEntries(window.localStorage);
    const sessionEntries = listScopedEntries(window.sessionStorage);
    const localPayload = JSON.stringify(localEntries);
    const sessionPayload = JSON.stringify(sessionEntries);
    const localKey = `${LOCAL_SNAPSHOT_PREFIX}${userId}`;
    const sessionKey = `${SESSION_SNAPSHOT_PREFIX}${userId}`;

    if (localPayload.length > MAX_SNAPSHOT_BYTES) {
      // Too big to safely store. Wipe any previous (smaller) snapshot
      // for this user so we don't half-restore on next login.
      safeRemove(window.localStorage, localKey);
    } else if (!safeSet(window.localStorage, localKey, localPayload)) {
      // Quota hit even though the candidate looked fine — try to
      // recover space by dropping THIS user's previous snapshots first,
      // then retrying once. If it still fails we just drop it.
      safeRemove(window.localStorage, localKey);
      safeRemove(window.localStorage, sessionKey);
      safeSet(window.localStorage, localKey, localPayload);
    }

    if (sessionPayload.length > MAX_SNAPSHOT_BYTES) {
      safeRemove(window.localStorage, sessionKey);
    } else {
      safeSet(window.localStorage, sessionKey, sessionPayload);
    }
  } catch (e) {
    try {
      // eslint-disable-next-line no-console
      console.warn('[storageScope] persistSnapshot failed; auth flow continues unaffected', e);
    } catch { /* ignore */ }
  }
}

function restoreSnapshot(userId) {
  if (!userId) return;
  // Same defensive posture as persistSnapshot: a corrupt or oversized
  // snapshot must not be able to break auth. We restore key-by-key
  // and tolerate per-key failures so the user gets at least a partial
  // recovery instead of a hard logout.
  let localRaw = null;
  let sessionRaw = null;
  try {
    localRaw = window.localStorage.getItem(`${LOCAL_SNAPSHOT_PREFIX}${userId}`);
    sessionRaw = window.localStorage.getItem(`${SESSION_SNAPSHOT_PREFIX}${userId}`);
  } catch { /* ignore — Safari Private Mode etc. */ }

  try {
    const localEntries = localRaw ? JSON.parse(localRaw) : {};
    Object.entries(localEntries).forEach(([k, v]) => safeSet(window.localStorage, k, v));
  } catch (e) {
    try {
      // eslint-disable-next-line no-console
      console.warn('Failed to restore localStorage snapshot', e);
    } catch { /* ignore */ }
  }

  try {
    const sessionEntries = sessionRaw ? JSON.parse(sessionRaw) : {};
    Object.entries(sessionEntries).forEach(([k, v]) => safeSet(window.sessionStorage, k, v));
  } catch (e) {
    try {
      // eslint-disable-next-line no-console
      console.warn('Failed to restore sessionStorage snapshot', e);
    } catch { /* ignore */ }
  }
}

export function installStorageScoping() {
  // Intentionally a no-op. We avoid monkey-patching Storage methods.
}

function hasSnapshot(userId) {
  if (!userId) return false;
  return (
    window.localStorage.getItem(`${LOCAL_SNAPSHOT_PREFIX}${userId}`) != null ||
    window.localStorage.getItem(`${SESSION_SNAPSHOT_PREFIX}${userId}`) != null
  );
}

export function setStorageScopeUser(userId) {
  if (typeof window === 'undefined') return;
  const nextUserId = userId == null ? '' : String(userId);
  if (!nextUserId) return;

  // Top-level safety net. Auth callers (AuthContext.activateUserStorageScope)
  // do NOT expect this function to ever throw. Any storage exception that
  // escapes here is observed by the user as a hard sign-in bounce — see
  // the May 27 2026 QuotaExceededError regression.
  try {
    let previousUserId = null;
    try { previousUserId = window.localStorage.getItem(ACTIVE_USER_KEY); } catch { /* ignore */ }

    if (previousUserId && previousUserId !== nextUserId) {
      persistSnapshot(previousUserId);
      clearScopedEntries(window.localStorage);
      clearScopedEntries(window.sessionStorage);
    } else if (!previousUserId && !hasSnapshot(nextUserId)) {
      // Truly first scoped login for this user (no snapshot yet) — capture
      // whatever pre-scope data is in the browser as the initial snapshot
      // so they don't lose existing preferences. Subsequent re-logins MUST
      // NOT hit this branch: previously they did, which overwrote the saved
      // snapshot with the just-cleared (empty) state and wiped scoped
      // preferences like the active sub-tab inside Agentic AI / Analytics.
      persistSnapshot(nextUserId);
    }

    restoreSnapshot(nextUserId);
    safeSet(window.localStorage, ACTIVE_USER_KEY, nextUserId);
    safeSet(window.sessionStorage, ACTIVE_USER_KEY, nextUserId);
  } catch (e) {
    try {
      // eslint-disable-next-line no-console
      console.warn('[storageScope] setStorageScopeUser failed; auth flow continues unaffected', e);
    } catch { /* ignore */ }
  }
}

export function migrateLegacyCachesForUser(userId) {
  // Legacy migration is handled by snapshot capture/restore in setStorageScopeUser.
  setStorageScopeUser(userId);
}

export function clearStorageScopeUser() {
  if (typeof window === 'undefined') return;
  // Same safety net — never throw out of here.
  try {
    let activeUserId = null;
    try { activeUserId = window.localStorage.getItem(ACTIVE_USER_KEY); } catch { /* ignore */ }
    if (activeUserId) {
      persistSnapshot(activeUserId);
    }
    clearScopedEntries(window.localStorage);
    clearScopedEntries(window.sessionStorage);
    safeRemove(window.localStorage, ACTIVE_USER_KEY);
    safeRemove(window.sessionStorage, ACTIVE_USER_KEY);
  } catch (e) {
    try {
      // eslint-disable-next-line no-console
      console.warn('[storageScope] clearStorageScopeUser failed; auth flow continues unaffected', e);
    } catch { /* ignore */ }
  }
}
